import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageHeader, Panel } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const TEMPLATE = "title,category,industry,description,requirements,budget,currency,country,city,source,credit_cost,client_name,phone,email,whatsapp\n" +
  "Website redesign for dental clinic,Web Development,Healthcare,Modern responsive website with booking,5 pages + booking form,80000,INR,India,Chennai,Referral,5,Jane Doe,+91 98000 00000,jane@example.com,+91 98000 00000\n";

export default function ImportCsv() {
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [verified, setVerified] = useState(false);
  const [publish, setPublish] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef();

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    f.text().then(setText);
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/import", { csv_text: text, mark_verified: verified, publish: verified && publish });
      setResult(data);
      toast.success(`Imported ${data.imported} leads`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const template = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    a.download = "lead-import-template.csv";
    a.click();
  };

  return (
    <div>
      <PageHeader eyebrow="Bulk upload" title="CSV import" subtitle="Duplicates are detected by client email and phone — both within the file and against existing leads."
        actions={<Button variant="outline" onClick={template} data-testid="download-template-button"><Download className="h-4 w-4 mr-2" />Template</Button>} />
      <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
        <div onClick={() => input.current.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
          className="cursor-pointer rounded-xl border-2 border-dashed bg-card p-12 text-center hover:border-primary/50 transition-colors fade-up" data-testid="admin-csv-import-dropzone">
          <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => pick(e.target.files[0])} data-testid="csv-file-input" />
          {file ? <FileText className="h-10 w-10 mx-auto text-primary" /> : <UploadCloud className="h-10 w-10 mx-auto text-muted-foreground" />}
          <p className="font-display font-semibold mt-4">{file ? file.name : "Drop a CSV file or click to browse"}</p>
          <p className="text-xs text-muted-foreground mt-1">{file ? `${text.split("\n").filter(Boolean).length - 1} rows detected` : "Required: title, category, description, budget, country, client_name + phone or email"}</p>
        </div>
        <Panel title="Import options">
          <div className="space-y-4">
            <div className="flex items-center justify-between"><Label htmlFor="v">Mark rows as verified</Label><Switch id="v" checked={verified} onCheckedChange={setVerified} data-testid="import-verified-switch" /></div>
            <div className="flex items-center justify-between"><Label htmlFor="p" className={verified ? "" : "text-muted-foreground"}>Publish immediately</Label><Switch id="p" disabled={!verified} checked={publish && verified} onCheckedChange={setPublish} data-testid="import-publish-switch" /></div>
            <Button className="w-full" disabled={!text || busy} onClick={run} data-testid="run-import-button">{busy ? "Importing..." : "Import leads"}</Button>
          </div>
        </Panel>
      </div>
      {result && (
        <Panel title="Import summary" className="mt-6">
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div><p className="text-xs text-muted-foreground uppercase">Imported</p><p className="font-display text-2xl font-bold text-emerald-600" data-testid="import-count">{result.imported}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase">Duplicates</p><p className="font-display text-2xl font-bold text-amber-600" data-testid="import-duplicates">{result.duplicates.length}</p></div>
            <div><p className="text-xs text-muted-foreground uppercase">Errors</p><p className="font-display text-2xl font-bold text-rose-600" data-testid="import-errors">{result.errors.length}</p></div>
          </div>
          {[...result.duplicates.map((d) => ({ ...d, kind: "Duplicate", msg: `${d.title} — ${d.reason}` })), ...result.errors.map((e) => ({ ...e, kind: "Error", msg: e.error }))].map((r, i) => (
            <p key={i} className="text-sm py-1.5 border-b last:border-0"><span className="font-mono text-xs text-muted-foreground">Row {r.row}</span> · <span className="font-medium">{r.kind}</span> · {r.msg}</p>
          ))}
        </Panel>
      )}
    </div>
  );
}
